// src/auth/msalConfig.ts — SCE Issues
// Configuration MSAL pour l'authentification Azure AD
//
// ⚠️ Les valeurs client_id et tenant_id doivent être fournies par l'IT Sartorius.
// Remplacer les valeurs dans .env.local quand l'IT répond.

import {
  PublicClientApplication,
  LogLevel,
  type Configuration,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser'

// ─── Variables d'environnement typées ───

const clientId = import.meta.env.VITE_AZURE_CLIENT_ID ?? ''
const tenantId = import.meta.env.VITE_AZURE_TENANT_ID ?? ''
const redirectUri = import.meta.env.VITE_REDIRECT_URI ?? window.location.origin

if (!clientId || !tenantId) {
  // eslint-disable-next-line no-console
  console.warn(
    '[MSAL] VITE_AZURE_CLIENT_ID / VITE_AZURE_TENANT_ID non définis. ' +
    'L\'authentification ne fonctionnera pas tant que l\'IT n\'a pas fourni ces valeurs.'
  )
}

// ─── Configuration principale MSAL ───

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // localStorage (Lot 10) : partagé par les onglets et gardé à la fermeture
    // du navigateur. En sessionStorage, chaque lien ouvert depuis un mail ou
    // Teams (onglet neuf) redemandait la connexion. Quasi aucun poste partagé
    // à Pompey (décision de Guillaume du 25/09) ; cf. auth/session.ts.
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        // En dev uniquement : log MSAL
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          switch (level) {
            case LogLevel.Error:
              console.error('[MSAL]', message)
              break
            case LogLevel.Warning:
              console.warn('[MSAL]', message)
              break
            case LogLevel.Info:
              console.info('[MSAL]', message)
              break
            default:
              break
          }
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      piiLoggingEnabled: false,
    },
  },
}

// ─── Scopes Graph API requis ───

/**
 * Scopes demandés pour l'accès Graph API.
 * - User.Read : lire le profil de l'utilisateur courant
 * - Sites.ReadWrite.All : lire/écrire dans les listes SharePoint
 * - Files.ReadWrite.All : upload de pièces jointes
 */
export const graphScopes = {
  base: ['User.Read'],
  sharepoint: ['Sites.ReadWrite.All'],
  files: ['Files.ReadWrite.All'],
  all: ['User.Read', 'Sites.ReadWrite.All', 'Files.ReadWrite.All'],
} as const

/**
 * Hostname SharePoint Sartorius — sert d'audience pour les tokens SP REST.
 * Doit être identique à l'URL racine du tenant (sans protocole, sans /sites/...).
 */
export const SHAREPOINT_HOSTNAME = 'sartorius.sharepoint.com'

/**
 * Scopes pour l'API SharePoint REST (différente de Graph).
 *
 * On utilise `.default` plutôt qu'un scope nommé (AllSites.Write, etc.) :
 *   → MSAL demande automatiquement tous les scopes SharePoint déjà accordés
 *     à l'app dans Azure AD, sans qu'on ait à les énumérer ici.
 *   → Si l'IT a déjà accordé AllSites.Write côté app registration, ça
 *     fonctionnera sans interaction. Sinon, MSAL demandera le consentement.
 *
 * ⚠️ L'audience SharePoint REST est DIFFÉRENTE de Graph
 *    → on aura DEUX tokens distincts en mémoire MSAL.
 */
export const sharepointScopes = {
  default: [`https://${SHAREPOINT_HOSTNAME}/.default`],
} as const

// ─── Requêtes types ───

export const loginRequest: RedirectRequest = {
  scopes: [...graphScopes.all],
  prompt: 'select_account',
}

export const tokenRequest: SilentRequest = {
  scopes: [...graphScopes.all],
  forceRefresh: false,
}

/**
 * Token request dédié SharePoint REST (audience différente de Graph).
 *
 * Utilisé par sharepointClient pour récupérer un token avec audience
 * https://sartorius.sharepoint.com (au lieu de https://graph.microsoft.com).
 *
 * MSAL gère automatiquement deux tokens parallèles en cache.
 */
export const sharepointTokenRequest: SilentRequest = {
  scopes: [...sharepointScopes.default],
  forceRefresh: false,
}

// ─── Instance MSAL partagée ───
// Initialisée par demarrerSession() (auth/session.ts), requis en v3.

export const msalInstance = new PublicClientApplication(msalConfig)
